/**
 * Schmaler fetch-Wrapper für API-Aufrufe.
 *
 * - Hängt automatisch den Bearer-Token an, falls vorhanden.
 * - Wirft `ApiError` mit Status + JSON-Body bei nicht-OK-Response.
 * - Im Dev läuft alles über den Vite-Proxy, im Prod über denselben Origin.
 */

const BASE = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') ?? '';

/**
 * Aktive Firma (Mandant). Wird vom Auth-Store gesetzt und bei jedem Request als
 * `X-Company-Id`-Header mitgeschickt. null = „alle Firmen" (nur Admin) bzw. beim
 * Nicht-Admin fällt der Server auf die erste Mitgliedschaft zurück.
 */
let activeCompanyId: string | null = null;
export function setActiveCompanyHeader(id: string | null): void {
  activeCompanyId = id;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

/** Event-Name, den apiFetch auslöst, wenn der Server ein eigenes Passwort verlangt (Kapitel 3.2). */
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'shiftsync:password-change-required';

/**
 * Gesamtprüfung 11.09.2026: Sitzung ist serverseitig ungültig (401 trotz
 * mitgeschicktem Token — z. B. „Überall abmelden“, Passwort zurückgesetzt,
 * Konto deaktiviert). App.tsx meldet dann ab, statt die App scheinbar
 * eingeloggt, aber funktionslos stehen zu lassen.
 */
export const UNAUTHORIZED_EVENT = 'shiftsync:unauthorized';

interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  token?: string | null;
}

export async function apiFetch<T = unknown>(path: string, options: ApiOptions = {}): Promise<T> {
  const { token, body, headers, ...rest } = options;
  const finalHeaders = new Headers(headers);
  if (body !== undefined && !finalHeaders.has('content-type')) {
    finalHeaders.set('content-type', 'application/json');
  }
  if (token) {
    finalHeaders.set('authorization', `Bearer ${token}`);
  }
  if (!finalHeaders.has('x-company-id')) {
    finalHeaders.set('x-company-id', activeCompanyId ?? 'all');
  }

  const url = path.startsWith('http') ? path : `${BASE}${path}`;
  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // 204 No Content → kein JSON-Body
  if (res.status === 204) {
    return undefined as T;
  }

  let data: unknown;
  try {
    data = await res.json();
  } catch {
    data = null;
  }

  if (!res.ok) {
    // Nur echte Sitzungs-Fehler der Auth-Middleware (error: 'Unauthorized') —
    // NICHT z. B. „Aktuelles Passwort ist falsch“ (ebenfalls 401, AppError).
    if (
      res.status === 401 &&
      token &&
      typeof window !== 'undefined' &&
      data &&
      typeof data === 'object' &&
      (data as Record<string, unknown>).error === 'Unauthorized'
    ) {
      window.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
    }
    // Kapitel 3.2 (Screen 7): Server verlangt erst ein eigenes Passwort
    // (z. B. weil Chef/Filialleitung es gerade neu gesetzt hat, während diese
    // Sitzung noch offen war). App.tsx lauscht darauf, lädt /api/auth/me neu
    // und leitet auf „Passwort festlegen" um.
    if (
      res.status === 403 &&
      data &&
      typeof data === 'object' &&
      (data as Record<string, unknown>).error === 'PasswordChangeRequired' &&
      typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new Event(PASSWORD_CHANGE_REQUIRED_EVENT));
    }
    const msg =
      (data && typeof data === 'object' && 'message' in data && typeof (data as Record<string, unknown>).message === 'string'
        ? ((data as Record<string, unknown>).message as string)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }

  return data as T;
}
